import {
	type AstNodeDescription,
	AstUtils,
	DefaultScopeProvider,
	EMPTY_SCOPE,
	type ReferenceInfo,
	type Scope,
} from "langium";

import { BUILTIN_LIBRARY_URI } from "../builtin/index.js";
import {
	Directive,
	type File,
	type Import,
	type ImportMember,
	Module,
	type ReferenceDirective,
	type ReferenceDirectiveArgument,
	type ReferenceType,
	type ReferenceTypeOption,
	Type,
	isImport,
	isImportMember,
	isImportable,
	isReferenceDirective,
	isReferenceDirectiveArgument,
	isReferenceType,
	isReferenceTypeOption,
	isType,
} from "../generated/ast.js";

export class IprotoScopeProvider extends DefaultScopeProvider {
	private getModuleScope() {
		const modules = this.indexManager.allElements(Module).filter((mod) => {
			if (mod.documentUri.toString() === BUILTIN_LIBRARY_URI) {
				return false;
			}

			return true;
		});

		return this.createScope(modules);
	}

	private getImportMemberScope(member: ImportMember) {
		const container = member.$container;

		if (container.target.ref) {
			const module = container.target.ref;
			const descriptions: AstNodeDescription[] = [];

			for (const decl of module.$container.declaraions) {
				if (isImportable(decl)) {
					descriptions.push(this.descriptions.createDescription(decl, decl.name));
				}
			}

			return this.createScope(descriptions);
		}

		return EMPTY_SCOPE;
	}

	private getDirectiveScope(container: ReferenceDirective) {
		const currentUri = AstUtils.getDocument<File>(container).uri.toString();

		const uris = new Set([currentUri, BUILTIN_LIBRARY_URI]);
		const descriptions = this.indexManager.allElements(Directive, uris).toArray();

		const document = AstUtils.getDocument<File>(container);

		for (const decl of document.parseResult.value.declaraions) {
			if (decl.$type === "Import") {
				for (const member of decl.members) {
					if (member.target?.ref && member.target.ref.$type === "Directive") {
						if (member.name) {
							descriptions.push(this.descriptions.createDescription(member, member.name));
						} else {
							descriptions.push(this.descriptions.createDescription(member.target.ref, member.target.$refText));
						}
					}
				}
			}
		}

		return this.createScope(descriptions);
	}

	private getDirectiveArgumentScope(container: ReferenceDirectiveArgument) {
		const reference = container.$container;
		const directive = reference.target.ref;

		if (directive) {
			return this.createScope(
				directive.parameters.map((param) => {
					return this.descriptions.createDescription(param, param.name);
				}),
			);
		}

		return EMPTY_SCOPE;
	}

	private getTypeScope(container: ReferenceType) {
		const currentUri = AstUtils.getDocument<File>(container).uri.toString();

		const uris = new Set([currentUri, BUILTIN_LIBRARY_URI]);
		const descriptions = this.indexManager.allElements(Type, uris).toArray();

		const document = AstUtils.getDocument<File>(container);

		for (const decl of document.parseResult.value.declaraions) {
			if (decl.$type === "Import") {
				for (const member of decl.members) {
					const type = member.target?.ref;
					if (isType(type)) {
						if (member.name) {
							descriptions.push(this.descriptions.createDescription(type, member.name));
						} else {
							descriptions.push(this.descriptions.createDescription(type, member.target.$refText));
						}
					}
				}
			}
		}

		return this.createScope(descriptions);
	}

	private getScalarOptionScope(container: ReferenceTypeOption) {
		const reference = container.$container;
		const type = reference.target.ref;

		if (type && type.$type === "Scalar") {
			return this.createScope(type.options.map((option) => this.descriptions.createDescription(option, option.name)));
		}

		return EMPTY_SCOPE;
	}

	override getScope(context: ReferenceInfo): Scope {
		if (isReferenceDirective(context.container)) {
			const property = context.property as keyof ReferenceDirective;

			if (property === "target") {
				return this.getDirectiveScope(context.container);
			}
		}

		if (isReferenceDirectiveArgument(context.container)) {
			const property = context.property as keyof ReferenceDirectiveArgument;

			if (property === "target") {
				return this.getDirectiveArgumentScope(context.container);
			}
		}

		if (isReferenceType(context.container)) {
			const property = context.property as keyof ReferenceType;

			if (property === "target") {
				return this.getTypeScope(context.container);
			}
		}

		if (isReferenceTypeOption(context.container)) {
			const property = context.property as keyof ReferenceTypeOption;

			if (property === "target") {
				return this.getScalarOptionScope(context.container);
			}
		}

		if (isImport(context.container)) {
			const property = context.property as keyof Import;

			if (property === "target") {
				return this.getModuleScope();
			}
		}

		if (isImportMember(context.container)) {
			const property = context.property as keyof ImportMember;

			if (property === "target") {
				return this.getImportMemberScope(context.container);
			}
		}

		return EMPTY_SCOPE;
	}
}
